import React, { useState, useEffect, useCallback } from "react";
import BaseModal from "./BaseModal";
import { ChatMessage } from "../../types/chat";
import { useMessages } from "../../providers/MessagesContext";
import { processCustomTags, formatMessageContent } from "../../utils/textUtils";
import {
  getMediaFromChatMessage,
  getAudioFromChatMessage,
} from "../../services/ChatService";
import { MediaDisplay } from "../MediaDisplay";
import MessageControls from "../MessageControls";
import { useAudio } from "../../providers/AudioProvider";
import { useMessageActions } from "../../hooks/useMessageActions";
import OptionButtons from "../OptionButtons";

interface DefinitionModalProps {
  message: ChatMessage | null;
}

const DefinitionModal: React.FC<DefinitionModalProps> = ({ message }) => {
  const { removeDefinitionMessages, removeMessage } = useMessages();
  const { playResponseOutloud } = useAudio();
  const { onSpanClick } = useMessageActions();

  // Use pre-fetched media and audio if available, otherwise fall back to fetching
  const [mediaBlob, setMediaBlob] = useState<
    | Blob
    | {
        url: string;
        media_type: string;
        content_type: string;
        elapsed_time: number;
      }
    | null
  >(message?.preFetchedMediaBlob || null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(
    message?.preFetchedAudioBlob || null
  );
  const [mediaUrl, setMediaUrl] = useState<string | null>(
    message?.preFetchedMediaUrl || null
  );
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);

  const isOpen = !!message;

  // Fetch media and audio if not pre-fetched
  useEffect(() => {
    if (!message) return;

    const fetchMediaAndAudio = async () => {
      if (
        message.media &&
        message.messageType === "bot" &&
        !message.preFetchedMediaBlob
      ) {
        setIsLoadingMedia(true);
        try {
          const result = await getMediaFromChatMessage({
            id: message.id,
            messageId: message.messageId || message.id,
            text: message.text,
            dateTime: message.dateTime,
            messageType: message.messageType,
            media: message.media,
            audio: Array.isArray(message.audio)
              ? message.audio
              : [message.audio || ""],
          });
          setMediaBlob(result);

          if (result instanceof Blob) {
            setMediaUrl(URL.createObjectURL(result));
          } else if (result && typeof result === "object" && "url" in result) {
            setMediaUrl(result.url);
          }
        } catch (error) {
          console.error("Error fetching media:", error);
        } finally {
          setIsLoadingMedia(false);
        }
      }

      if (
        message.audio &&
        message.audio.length > 0 &&
        message.messageType === "bot" &&
        !message.preFetchedAudioBlob
      ) {
        setIsLoadingAudio(true);
        try {
          const result = await getAudioFromChatMessage({
            id: message.id,
            messageId: message.messageId || message.id,
            text: message.text,
            dateTime: message.dateTime,
            messageType: message.messageType,
            media: message.media || "",
            audio: message.audio,
          });
          setAudioBlob(result);
        } catch (error) {
          console.error("Error fetching audio:", error);
        } finally {
          setIsLoadingAudio(false);
        }
      }
    };

    fetchMediaAndAudio();
  }, [message]);

  // Cleanup function to revoke object URLs
  useEffect(() => {
    return () => {
      if (
        mediaUrl &&
        mediaBlob instanceof Blob &&
        !message?.preFetchedMediaUrl
      ) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [mediaUrl, mediaBlob, message?.preFetchedMediaUrl]);

  // Reset state when message changes
  useEffect(() => {
    if (message) {
      setMediaBlob(message.preFetchedMediaBlob || null);
      setAudioBlob(message.preFetchedAudioBlob || null);
      setMediaUrl(message.preFetchedMediaUrl || null);
    }
  }, [message]);

  // Display the content/text from the message along with any media. Show message controls to be able to flag this message (affects the logs only). If flagged, close the modal
  const closeDefinitionModal = useCallback(() => {
    removeDefinitionMessages();
  }, [removeDefinitionMessages]);

  // Handle span/virtual link clicks - add new message first, then remove old one to avoid flickering
  const handleSpanClick = useCallback(
    async (
      dataId: string,
      spanText: string,
      elementId: string,
      dataInfo: string
    ) => {
      if (!message) return;

      // Store the old message ID to remove after new message is added
      const oldMessageId = message.id;

      // Add the new definition message first (onSpanClick handles this)
      await onSpanClick(dataId, spanText, elementId, dataInfo);

      // Remove the old definition message after the new one is added
      // Use a small delay to ensure the new message is in the state
      setTimeout(() => {
        removeMessage(oldMessageId);
      }, 100);
    },
    [message, onSpanClick, removeMessage]
  );

  // Close modal if message is flagged
  useEffect(() => {
    if (message?.isFlagged) {
      closeDefinitionModal();
    }
  }, [message?.isFlagged, closeDefinitionModal]);

  if (!message) return null;

  const formattedContent = formatMessageContent(message.text, true);
  const term = message.origin;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={closeDefinitionModal}
      title={term ? `${processCustomTags(term)}` : "Definition"}
      lastUpdated=""
      allowBackdropClose={true}
      showCloseButton={true}
      maxWidth="max-w-2xl"
      maxHeight="max-h-[85vh]"
    >
      <div className="space-y-4">
        {/* Message Content */}
        <div
          className="text-sm lg:text-base leading-relaxed whitespace-pre-wrap text-gray-800"
          dangerouslySetInnerHTML={{ __html: formattedContent }}
          data-trust-trigger="message-content"
          onClick={async (e) => {
            // Handle clicks on custom-link spans
            const target = e.target as HTMLElement;
            if (target.classList.contains("custom-link")) {
              const dataId = target.getAttribute("data-id");
              const dataInfo = target.getAttribute("data-info");
              const spanText = target.textContent;
              if (dataId && spanText && message && dataInfo) {
                e.preventDefault();
                await handleSpanClick(
                  dataId,
                  spanText,
                  message.id || "",
                  dataInfo
                );
              }
            }
          }}
        />

        {/* Media Display */}
        {message.media && (mediaUrl || isLoadingMedia) && (
          <MediaDisplay
            mediaUrl={mediaUrl}
            onVideoEnd={() => {}}
            mediaId={message.messageId || message.id}
            originalMediaPath={message.media}
            onMediaUrlUpdate={(newUrl) => {
              setMediaUrl(newUrl);
              setMediaBlob({
                url: newUrl,
                media_type: "video",
                content_type: "video/mp4",
                elapsed_time: 0,
              });
            }}
          />
        )}

        {/* option buttons */}
        {message.options && message.options.length > 0 && (
          <OptionButtons
            options={message.options}
            parentNodeUuid={message.nodeUuid || ""}
            elementId={message.messageId || message.id}
            isDefinitionNode={true}
          />
        )}

        {/* Message Controls - positioned at bottom right */}
        <div className="flex justify-end items-end pt-2">
          <MessageControls
            audioBlob={audioBlob}
            isLoadingAudio={isLoadingAudio}
            queryId={message.messageId || message.id}
            autoPlay={playResponseOutloud}
            isFlagged={message.isFlagged}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default DefinitionModal;
